import { useState, useCallback, useEffect } from 'react';
import { useFileUpload } from './useFileUpload';
import { extractUrlsFromDataTransfer } from '../utils/fileDownloader';

interface UseGlobalDragAndDropReturn {
  isDragOver: boolean;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  isUploading: boolean;
}

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/aces',
  'image/exr',
  'image/x-portable-bitmap',
  'image/x-portable-graymap',
  'image/x-portable-pixmap',
  'image/x-cmu-raster',
  'image/x-xbitmap',
  'image/x-rgb',
];

const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.gif',
  '.jpeg',
  '.jpg',
  '.png',
  '.webp',
  '.bmp',
  '.tiff',
  '.exr',
  '.pbm',
  '.pgm',
  '.ppm',
  '.rast',
  '.xbm',
  '.rgb',
];

function isAcceptedFile(fileOrItem: { type?: string; name?: string }): boolean {
  if (fileOrItem.type && ACCEPTED_MIME_TYPES.includes(fileOrItem.type)) {
    return true;
  }
  if (fileOrItem.name) {
    const lower = fileOrItem.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
  }
  return false;
}

export const useGlobalDragAndDrop = (): UseGlobalDragAndDropReturn => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [_dragCounter, setDragCounter] = useState(0);
  const { uploadFiles, isUploading, uploadFilesFromUrls } = useFileUpload();

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isUploading) {
        setIsDragOver(false);
        return;
      }

      let hasValidContent = false;

      // Vérifier les fichiers
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        const hasValidFiles = Array.from(e.dataTransfer.items).some((item) =>
          isAcceptedFile(item)
        );
        if (hasValidFiles) {
          hasValidContent = true;
        }
      } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const hasValidFiles = Array.from(e.dataTransfer.files).some((file) =>
          isAcceptedFile(file)
        );
        if (hasValidFiles) {
          hasValidContent = true;
        }
      }

      // Vérifier les types de données pour les liens
      if (!hasValidContent) {
        const dataTypes = e.dataTransfer.types;
        // Accepter tous les types de texte qui pourraient contenir des URLs
        const textTypes = [
          'text/uri-list',
          'text/plain',
          'text/html',
          'text/url',
        ];
        if (textTypes.some((type) => dataTypes.includes(type))) {
          hasValidContent = true;
        }

        // Si aucun type spécifique n'est détecté mais qu'il y a des données, accepter quand même
        // (certains navigateurs ne définissent pas correctement les types)
        if (!hasValidContent && dataTypes.length > 0) {
          hasValidContent = true;
        }
      }

      if (hasValidContent) {
        setDragCounter((prev) => prev + 1);
        setIsDragOver(true);
      }
    },
    [isUploading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter <= 0) {
        setIsDragOver(false);
        return 0;
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragOver(false);
      setDragCounter(0);

      if (isUploading) {
        return;
      }

      // Traiter les fichiers
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        await uploadFiles(files);
      }

      // Traiter les liens PDF
      try {
        const pdfUrls = extractUrlsFromDataTransfer(e.dataTransfer);
        if (pdfUrls.length > 0) {
          const uploadFn = uploadFilesFromUrls as (
            urls: string[]
          ) => Promise<void>;
          await uploadFn(pdfUrls);
        }
      } catch (error) {
        console.error(
          'Erreur lors du traitement des liens PDF:',
          error instanceof Error ? error.message : 'Erreur inconnue'
        );
      }
    },
    [uploadFiles, uploadFilesFromUrls, isUploading]
  );

  useEffect(() => {
    const handleGlobalDragLeave = (e: DragEvent) => {
      if (
        e.clientX <= 0 ||
        e.clientY <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        setIsDragOver(false);
        setDragCounter(0);
      }
    };

    document.addEventListener('dragleave', handleGlobalDragLeave);

    return () => {
      document.removeEventListener('dragleave', handleGlobalDragLeave);
    };
  }, []);

  return {
    isDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragOver,
    isUploading,
  };
};
